import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import { ComunicacionAjax } from "../../ts/api/comunicacion";
import { useAuth } from "../../hooks/useAuth";
import { useQueryClient } from "@tanstack/react-query";
import { useNFC } from "../../hooks/useNFC";

export function LoginForm() {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { setAuth } = useAuth();
	const [error, setError] = useState<string | null>(null);
	const nfc = useNFC();

	const onSubmit = useCallback(async (e: React.FormEvent) => {
		e.preventDefault();
		const form = e.target as HTMLFormElement;
		const email = (form.elements.namedItem("email") as HTMLInputElement).value;
		const password = (form.elements.namedItem("password") as HTMLInputElement).value;

		try {
			const res = await login(email, password);
			if (res.status === "error") {
				setError(res.message ?? "Error desconocido al autenticar");
				return;
			}
			if (res.status === "NOK") {
				setError(res.message ?? "Error desconocido al autenticar");
				return;
			}

			setAuth(res.token);
			void queryClient.invalidateQueries();// Invalidate all queries to refresh data after login
			await navigate(res.goTo);
		} catch (error) {
			setError("Error en el login");
			console.error(error);
		}
	}, [navigate, queryClient, setAuth]);
	const onSubmitNFC = useCallback(async (data: { email: string; hash: string; }) => {
		try {
			const res = await loginNFC(data.email, data.hash);
			if (res.status === "error") {
				setError(res.message ?? "Error desconocido al autenticar con NFC");
				return;
			}
			if (res.status === "NOK") {
				setError(res.message ?? "Error desconocido al autenticar con NFC");
				return;
			}

			setAuth(res.token);
			void queryClient.invalidateQueries();// Invalidate all queries to refresh data after login
			await navigate(res.goTo);
		} catch (error) {
			setError("Error en el login con NFC: " + (error as Error).message);
			console.error(error);
		}
	}, [navigate, queryClient, setAuth]);

	useEffect(() => {
		if (nfc.data) {
			const data = nfc.data as { email: string; hash: string; };
			void onSubmitNFC(data);
		}
	}, [nfc.data, onSubmitNFC]);

	return (
		<form role="form" className="login-form" onSubmit={(e) => void onSubmit(e)}>
			{error ? (
				<div id="contenedorErrores" style={{fontWeight: "bold", color: "red", textAlign: "center", padding: "1em"}}>
					<div className="error">{error}</div>
				</div>
			) : null}

			<div className="form-group">
				<input type="email" className="form-control" name="email" placeholder="Email" autoComplete="username" />
			</div>
			<div className="form-group">
				<input type="password" className="form-control" name="password" placeholder="Password" autoComplete="current-password" />
			</div>

			<button type="submit" className="btnPrimary" style={{ width: "100%", marginBottom: 10 }}>Login</button>
			{nfc.nfcSupported == true && (
				nfc.isReading == true ? (
					<button type="button" className="btnPrimary" style={{ width: "100%", marginBottom: 10 }} onClick={() => nfc.stopNFCReading()}>Cancelar</button>
				) : (
					<button type="button" className="btnPrimary" style={{ width: "100%", marginBottom: 10 }} onClick={() => void nfc.startNFCReading()}>Leer NFC</button>
				)
			)}

			<Link to="/recuperarcontrasena">¿Olvidaste la contraseña? Haz click aquí</Link>
		</form>
	);
}
function login(email: string, password: string) {
	return ComunicacionAjax.sendAjaxRequest<LoginResponse>("POST", "/api/v1/public/login", { username: email, password: password, plataforma: "web" });
}
function loginNFC(email: string, nfctag: string) {
	return ComunicacionAjax.sendAjaxRequest<LoginResponse>("POST", "/api/v1/public/loginFromNFCTag", { email, nfctag, plataforma: "web" });
}


type LoginResponse = {
	status: "error";
	message?: string;
} | {
	status: "NOK";
	message?: string;
} | {
	status: "OK",
	roleName: "Administrador",
	goTo: string,
	token: string,
};